import type { Directive } from 'vue';

/**
 * 本地化 twemoji：把文本中的彩色 emoji 替换成打包后的 SVG 图片。
 *
 * 背景：emoji 依赖系统彩色字体渲染，Win10/11 自带 Segoe UI Emoji，
 * 但 Win7 没有该字体，emoji 会退化成「豆腐块」。这里用图片替换的方式
 * （即 twemoji 方案）保证各系统显示一致。为规避国内访问 twemoji CDN 慢/被墙，
 * 图标资源全部本地打包（src/assets/twemoji/*.svg），不走网络。
 *
 * 扩展方式：新增图标时，下载对应 SVG 到 src/assets/twemoji/，再在下方 emojiMap 登记
 * 「emoji 字符 → 资源地址」即可；未登记的 emoji 保持原样（不会出现裂图）。
 */

// emoji 字符 → 打包后的 SVG 资源地址（文件名为 twemoji 码位，如 🎁 = 1f381.svg）
// 由脚本按全项目扫描结果生成；新增图标时下载对应 SVG 到 assets/twemoji 后在此登记
const emojiMap: Record<string, string> = {
    '🔒': new URL('@/assets/twemoji/1f512.svg', import.meta.url).href,
    '📦': new URL('@/assets/twemoji/1f4e6.svg', import.meta.url).href,
    '🚪': new URL('@/assets/twemoji/1f6aa.svg', import.meta.url).href,
    '🎁': new URL('@/assets/twemoji/1f381.svg', import.meta.url).href,
    '🎉': new URL('@/assets/twemoji/1f389.svg', import.meta.url).href,
    '✅': new URL('@/assets/twemoji/2705.svg', import.meta.url).href,
    '⚡': new URL('@/assets/twemoji/26a1.svg', import.meta.url).href,
    '🔥': new URL('@/assets/twemoji/1f525.svg', import.meta.url).href,
    '🛒': new URL('@/assets/twemoji/1f6d2.svg', import.meta.url).href,
    '💬': new URL('@/assets/twemoji/1f4ac.svg', import.meta.url).href,
    '⬇️': new URL('@/assets/twemoji/2b07.svg', import.meta.url).href,
    '⚙️': new URL('@/assets/twemoji/2699.svg', import.meta.url).href,
    '👋': new URL('@/assets/twemoji/1f44b.svg', import.meta.url).href,
    '📋': new URL('@/assets/twemoji/1f4cb.svg', import.meta.url).href,
    '🔑': new URL('@/assets/twemoji/1f511.svg', import.meta.url).href,
    '⏳': new URL('@/assets/twemoji/23f3.svg', import.meta.url).href,
    '🌐': new URL('@/assets/twemoji/1f310.svg', import.meta.url).href,
    '💡': new URL('@/assets/twemoji/1f4a1.svg', import.meta.url).href,
    '🏠': new URL('@/assets/twemoji/1f3e0.svg', import.meta.url).href,
    '📝': new URL('@/assets/twemoji/1f4dd.svg', import.meta.url).href,
    '📊': new URL('@/assets/twemoji/1f4ca.svg', import.meta.url).href,
    '📧': new URL('@/assets/twemoji/1f4e7.svg', import.meta.url).href,
    '📓': new URL('@/assets/twemoji/1f4d3.svg', import.meta.url).href,
    '🗄️': new URL('@/assets/twemoji/1f5c4.svg', import.meta.url).href,
    '🚀': new URL('@/assets/twemoji/1f680.svg', import.meta.url).href,
    '🆓': new URL('@/assets/twemoji/1f193.svg', import.meta.url).href,
    '📁': new URL('@/assets/twemoji/1f4c1.svg', import.meta.url).href,
    '📄': new URL('@/assets/twemoji/1f4c4.svg', import.meta.url).href,
    '🖼️': new URL('@/assets/twemoji/1f5bc.svg', import.meta.url).href,
    '🚫': new URL('@/assets/twemoji/1f6ab.svg', import.meta.url).href,
    '🐢': new URL('@/assets/twemoji/1f422.svg', import.meta.url).href,
    '🔓': new URL('@/assets/twemoji/1f513.svg', import.meta.url).href,
    '💾': new URL('@/assets/twemoji/1f4be.svg', import.meta.url).href,
    '📂': new URL('@/assets/twemoji/1f4c2.svg', import.meta.url).href,
    '🔐': new URL('@/assets/twemoji/1f510.svg', import.meta.url).href,
    '🔍': new URL('@/assets/twemoji/1f50d.svg', import.meta.url).href,
    '🗂️': new URL('@/assets/twemoji/1f5c2.svg', import.meta.url).href,
    '📞': new URL('@/assets/twemoji/1f4de.svg', import.meta.url).href,
    '✉️': new URL('@/assets/twemoji/2709.svg', import.meta.url).href,
    '📨': new URL('@/assets/twemoji/1f4e8.svg', import.meta.url).href,
    '📖': new URL('@/assets/twemoji/1f4d6.svg', import.meta.url).href,
    '💿': new URL('@/assets/twemoji/1f4bf.svg', import.meta.url).href,
    '💳': new URL('@/assets/twemoji/1f4b3.svg', import.meta.url).href,
    '🤖': new URL('@/assets/twemoji/1f916.svg', import.meta.url).href,
    '⚠️': new URL('@/assets/twemoji/26a0.svg', import.meta.url).href,
    '🧹': new URL('@/assets/twemoji/1f9f9.svg', import.meta.url).href,
    '🗑️': new URL('@/assets/twemoji/1f5d1.svg', import.meta.url).href,
    '🔄': new URL('@/assets/twemoji/1f504.svg', import.meta.url).href,
    '🪟': new URL('@/assets/twemoji/1fa9f.svg', import.meta.url).href,
    '♻️': new URL('@/assets/twemoji/267b.svg', import.meta.url).href,
    '🛡️': new URL('@/assets/twemoji/1f6e1.svg', import.meta.url).href,
    '❓': new URL('@/assets/twemoji/2753.svg', import.meta.url).href,
    '🤝': new URL('@/assets/twemoji/1f91d.svg', import.meta.url).href,
    '👤': new URL('@/assets/twemoji/1f464.svg', import.meta.url).href,
    '✂': new URL('@/assets/twemoji/2702.svg', import.meta.url).href,
    '🐌': new URL('@/assets/twemoji/1f40c.svg', import.meta.url).href,
    '📤': new URL('@/assets/twemoji/1f4e4.svg', import.meta.url).href,
    '✂️': new URL('@/assets/twemoji/2702.svg', import.meta.url).href,
    // 注意：'↔'(U+2194) 有意不登记——它在项目中仅作 "PDF↔Word/JPG↔PNG" 连接符，
    // 普通字体（Arial/宋体）在 Win7 上也能正常显示，替换成彩色图反而是视觉倒退
    '📏': new URL('@/assets/twemoji/1f4cf.svg', import.meta.url).href,
    '🖥️': new URL('@/assets/twemoji/1f5a5.svg', import.meta.url).href,
    '⬇': new URL('@/assets/twemoji/2b07.svg', import.meta.url).href,
    '📥': new URL('@/assets/twemoji/1f4e5.svg', import.meta.url).href,
    '📱': new URL('@/assets/twemoji/1f4f1.svg', import.meta.url).href,
    '👥': new URL('@/assets/twemoji/1f465.svg', import.meta.url).href,
    '☁️': new URL('@/assets/twemoji/2601.svg', import.meta.url).href,
    '🔢': new URL('@/assets/twemoji/1f522.svg', import.meta.url).href,
    '🎨': new URL('@/assets/twemoji/1f3a8.svg', import.meta.url).href,
    '✨': new URL('@/assets/twemoji/2728.svg', import.meta.url).href,
    '🎥': new URL('@/assets/twemoji/1f3a5.svg', import.meta.url).href,
    '☁': new URL('@/assets/twemoji/2601.svg', import.meta.url).href,
    '⭐': new URL('@/assets/twemoji/2b50.svg', import.meta.url).href,
    '✏️': new URL('@/assets/twemoji/270f.svg', import.meta.url).href,
    '📑': new URL('@/assets/twemoji/1f4d1.svg', import.meta.url).href,
    '📸': new URL('@/assets/twemoji/1f4f8.svg', import.meta.url).href,
    '🔗': new URL('@/assets/twemoji/1f517.svg', import.meta.url).href,
    '🧩': new URL('@/assets/twemoji/1f9e9.svg', import.meta.url).href,
    '✍️': new URL('@/assets/twemoji/270d.svg', import.meta.url).href,
    '📘': new URL('@/assets/twemoji/1f4d8.svg', import.meta.url).href,
    '📗': new URL('@/assets/twemoji/1f4d7.svg', import.meta.url).href,
    '📕': new URL('@/assets/twemoji/1f4d5.svg', import.meta.url).href,
    '🎯': new URL('@/assets/twemoji/1f3af.svg', import.meta.url).href,
    '🧠': new URL('@/assets/twemoji/1f9e0.svg', import.meta.url).href,
    '📅': new URL('@/assets/twemoji/1f4c5.svg', import.meta.url).href,
    '📈': new URL('@/assets/twemoji/1f4c8.svg', import.meta.url).href,
    '💻': new URL('@/assets/twemoji/1f4bb.svg', import.meta.url).href,
    '💼': new URL('@/assets/twemoji/1f4bc.svg', import.meta.url).href,
    '🎓': new URL('@/assets/twemoji/1f393.svg', import.meta.url).href,
    '🏭': new URL('@/assets/twemoji/1f3ed.svg', import.meta.url).href,
    '🏢': new URL('@/assets/twemoji/1f3e2.svg', import.meta.url).href,
    '👨‍👩‍👧': new URL('@/assets/twemoji/1f468-200d-1f469-200d-1f467.svg', import.meta.url).href,
    '🧾': new URL('@/assets/twemoji/1f9fe.svg', import.meta.url).href,
    '🦞': new URL('@/assets/twemoji/1f99e.svg', import.meta.url).href,
    '🛟': new URL('@/assets/twemoji/1f6df.svg', import.meta.url).href,
    '🍎': new URL('@/assets/twemoji/1f34e.svg', import.meta.url).href,
};

const emojiKeys = Object.keys(emojiMap);

// 用已登记的 emoji 组装匹配正则（按字符长度倒序，避免多码位 emoji 被短匹配截断）
function escapeRegExp(s: string): string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
const emojiPattern = emojiKeys
    .slice()
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp)
    .join('|');

function hasEmoji(text: string): boolean {
    return emojiKeys.some((e) => text.includes(e));
}

// 把含 emoji 的一段文本转成「文本 + <img> + 文本…」，整体包进一个内联 <span>。
// 用 span 包裹的原因：原来是「一个文本节点」，若直接替换成多个节点，
// 当父元素是 flex/grid 容器时，这些节点会各自变成独立的 flex item，破坏布局；
// 包成单个 span 后，父容器看到的仍是「一个子节点」，布局保持不变。
function buildFragment(text: string): HTMLSpanElement {
    const span = document.createElement('span');
    span.className = 'twemoji-text';
    const re = new RegExp(emojiPattern, 'g');
    let lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
        const emoji = m[0];
        if (m.index > lastIndex) {
            span.appendChild(document.createTextNode(text.slice(lastIndex, m.index)));
        }
        const img = document.createElement('img');
        img.className = 'twemoji';
        img.src = emojiMap[emoji];
        img.alt = emoji;
        img.setAttribute('draggable', 'false');
        span.appendChild(img);
        lastIndex = m.index + emoji.length;
    }
    if (lastIndex < text.length) {
        span.appendChild(document.createTextNode(text.slice(lastIndex)));
    }
    return span;
}

// 替换单个文本节点里的 emoji（无 emoji 则不动）
function replaceTextNode(node: Text): void {
    const parent = node.parentNode;
    if (!parent) return;
    const tag = parent.nodeName;
    if (tag === 'SCRIPT' || tag === 'STYLE') return;
    // 跳过内联 SVG 内的文本（<svg><text>…），避免往 SVG 里塞 <img> 破坏图形
    if (parent instanceof SVGElement) return;
    if (node.nodeValue && hasEmoji(node.nodeValue)) {
        parent.replaceChild(buildFragment(node.nodeValue), node);
    }
}

// 遍历元素下的所有文本节点，替换其中的 emoji
function parse(root: Node): void {
    if (!root || !emojiPattern) return;
    if (root.nodeType === Node.TEXT_NODE) {
        replaceTextNode(root as Text);
        return;
    }
    if (root.nodeType !== Node.ELEMENT_NODE) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const targets: Text[] = [];
    let node: Node | null;
    while ((node = walker.nextNode())) {
        if (node.nodeValue && hasEmoji(node.nodeValue)) targets.push(node as Text);
    }
    targets.forEach(replaceTextNode);
}

// v-twemoji：可选的显式用法（挂载/更新后扫描自身子树）；一般无需手动使用，见下方 initTwemoji 全局处理
export const twemoji: Directive<HTMLElement> = {
    mounted(el) {
        parse(el);
    },
    updated(el) {
        parse(el);
    },
};

/**
 * 全局启用 emoji 图片化：先整体扫描一次，再用 MutationObserver 监听后续 DOM 变化。
 * - 只监听 childList/subtree（新增节点）：路由切换、v-if/v-for 展开、toast 弹出等都会覆盖；
 *   不监听 characterData，避免干扰倒计时/价格等响应式文本的原地更新（这些文本不含 emoji）。
 * - 处理自身注入的节点前先断开 observer，避免死循环。
 */
export function initTwemoji(root: HTMLElement = document.body): void {
    if (!root || !emojiPattern) return;
    parse(root);

    const observer = new MutationObserver((mutations) => {
        observer.disconnect();
        for (const mut of mutations) {
            mut.addedNodes.forEach((n) => parse(n));
        }
        observer.observe(root, { childList: true, subtree: true });
    });
    observer.observe(root, { childList: true, subtree: true });
}
